// Handing a picture or a video to an app that shows them large itself
// (`ShellHost.viewer`, the iOS app), instead of opening the web's viewer.
//
// Promise:
// - Sent as `viewer`: whole URLs, the images to step through with where each
//   sits on the screen now (none for one out of sight), a video's position.
// - While the app shows it, the web answers `viewer-moved` with where that
//   image sits now (`viewer-rect`), for closing into it.
// - `viewer-closed` ends it: a video's position goes back to the inline
//   player (`onClosed`), and the focus to what was pressed (the image the app
//   closed on, else the opener) when nothing else took it. Not into an
//   editable body (it would bring up the keyboard).
// - Messages of another opening (another `id`) are ignored; a new opening
//   ends the one before (an app that never said it closed).
// - Files not sent yet (`blob:`) cannot be read by the app: `canHand` says
//   no, and the web's viewer opens them.
import {
  SHELL_CONTRACT_VERSION,
  type ShellRect,
  type ShellViewer,
  type ShellViewerItem,
} from "../../../shell/outside/contract";
import type { ShellHost } from "../../../shell/outside/outside";
import { quietFocus } from "./focus";
import type { GalleryItem } from "./gallery";

/** Whether `host` shows `src` large itself. */
export function canHand(
  host: ShellHost | null | undefined,
  src: string,
): host is ShellHost {
  return host?.viewer === true && !/^(blob|data):/i.test(src);
}

/** Where `el` sits in the viewport (CSS px), or undefined when out of sight. */
export function rectOf(el: Element | null | undefined): ShellRect | undefined {
  if (!el?.isConnected) return undefined;
  const r = el.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return undefined;
  if (
    r.right <= 0 ||
    r.bottom <= 0 ||
    r.left >= window.innerWidth ||
    r.top >= window.innerHeight
  )
    return undefined;
  return {
    x: Math.round(r.left),
    y: Math.round(r.top),
    w: Math.round(r.width),
    h: Math.round(r.height),
  };
}

const whole = (url: string) => new URL(url, window.location.href).href;

function newId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

// frontend-development: one opening per host at a time (each app has its own host); ending the one before when the next opens
const current = new WeakMap<ShellHost, () => void>();

export function handToApp(
  host: ShellHost,
  what: {
    kind: ShellViewer["kind"];
    items: readonly GalleryItem[];
    index: number;
    startAt?: number;
    opener: Element | null;
    onClosed?: (time: number | undefined) => void;
  },
): void {
  current.get(host)?.();
  const id = newId();
  const items: ShellViewerItem[] = what.items.map((item) => {
    const rect = rectOf(item.el);
    return {
      url: whole(item.src),
      name: item.name,
      ...(item.download ? { download: whole(item.download) } : {}),
      ...(item.author ? { author: item.author } : {}),
      ...(item.at ? { at: item.at } : {}),
      ...(rect ? { rect } : {}),
    };
  });
  const off = host.subscribe((message) => {
    if (message.type !== "viewer-moved" && message.type !== "viewer-closed")
      return;
    if (message.id !== id) return;
    const item = what.items[message.index];
    if (message.type === "viewer-moved") {
      const rect = rectOf(item?.el);
      host.post({
        version: SHELL_CONTRACT_VERSION,
        type: "viewer-rect",
        id,
        index: message.index,
        ...(rect ? { rect } : {}),
      });
      return;
    }
    end();
    what.onClosed?.(message.time);
    focusBack(item?.el?.isConnected ? item.el : what.opener, what.opener);
  });
  const end = () => {
    off();
    if (current.get(host) === end) current.delete(host);
  };
  current.set(host, end);
  host.post({
    version: SHELL_CONTRACT_VERSION,
    type: "viewer",
    id,
    kind: what.kind,
    index: what.index,
    items,
    ...(what.startAt !== undefined ? { startAt: what.startAt } : {}),
  });
}

/**
 * The focus on `el` when nothing else took it since the opening (it is on
 * nothing, or still on the opener), without a ring.
 */
function focusBack(el: Element | null, opener: Element | null) {
  if (!(el instanceof HTMLElement) || !el.isConnected) return;
  if (el.closest('[contenteditable="true"]')) return;
  const active = document.activeElement;
  if (active && active !== document.body && active !== opener) return;
  el.focus({ preventScroll: true });
  quietFocus(el);
}
